import type { UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import { UntypedFormBuilder } from '@angular/forms';
import type { CdkDragDrop } from '@angular/cdk/drag-drop';
import { moveItemInArray, CdkDropList, CdkDrag, CdkDropListGroup, CdkDragHandle } from '@angular/cdk/drag-drop';
import type { SelectedFileExtended } from '@common/types/file.type';

import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    type OnChanges,
    type OnInit,
    Output,
    type SimpleChanges,
    inject,
} from '@angular/core';
import { Subject } from 'rxjs';

import { NgIf, NgFor } from '@angular/common';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { SpSetupImageDropdownComponent } from '../sp-setup-image-dropdown/sp-setup-image-dropdown.component';
import { SpFileManagerContextService } from '../sp-file-manager-context.service';

@Component({
    selector: 'sp-image-preview-multiple',
    templateUrl: './sp-image-preview-multiple.component.html',
    styleUrls: ['./sp-image-preview-multiple.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        NgIf,
        CdkDropList,
        NgFor,
        CdkDrag,
        CdkDropListGroup,
        CdkDragHandle,
        BsDropdownModule,
        SpSetupImageDropdownComponent,
    ],
})
export class SpImagePreviewMultipleComponent implements OnInit, OnChanges {
    private readonly fb = inject(UntypedFormBuilder);
    protected readonly changeDetectorRef = inject(ChangeDetectorRef);
    public readonly ctx = inject(SpFileManagerContextService);

    @Input() public files: SelectedFileExtended[] = [];
    @Input() public isLink: boolean;
    @Input() public showSetupBtn: boolean = true;

    @Output() public setFileEvent: EventEmitter<SelectedFileExtended> = new EventEmitter<SelectedFileExtended>();
    @Output() public updateFilesEvent: EventEmitter<SelectedFileExtended[]> = new EventEmitter<
        SelectedFileExtended[]
    >();
    @Output() public replaceFileEvent: EventEmitter<number> = new EventEmitter<number>();
    @Output() public activateFileEvent: EventEmitter<number> = new EventEmitter<number>();
    @Output() public imageEditEvent: EventEmitter<number> = new EventEmitter<number>();

    public form: UntypedFormGroup;
    public closeSubject: Subject<void> = new Subject<void>();

    private readonly imagePreviewIndexes = new Map<string, number>();
    private readonly imagePreviewSourceKeys = new Map<string, string>();

    constructor() {
        this.initForm();
    }

    public ngOnInit(): void {
        this.setFilesData();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (!changes || !changes.files) {
            return;
        }

        const { files } = changes;
        if (files.previousValue && files.currentValue && files.previousValue.length < files.currentValue.length) {
            const diff: number = files.currentValue.length - files.previousValue.length;
            [...Array(diff).keys()].forEach(() => {
                this.filesForm.push(this.fb.control(null));
            });
        }

        this.filesForm.patchValue(changes.files.currentValue);
    }

    private initForm(): void {
        this.form = this.fb.group({
            files: this.fb.array([]),
        });
    }

    private setFilesData(): void {
        this.files.forEach((file: SelectedFileExtended) => {
            this.filesForm.push(this.fb.control(file));
        });
    }

    public get filesForm(): UntypedFormArray {
        return this.form.get('files') as UntypedFormArray;
    }

    public emitHiddenEventToChild(): void {
        this.closeSubject.next();
    }

    public removeMultipleFile(file: SelectedFileExtended): void {
        const key = this.getImagePreviewKey(file);

        this.imagePreviewIndexes.delete(key);
        this.imagePreviewSourceKeys.delete(key);
        const fileIdx: number = this.filesForm.value.findIndex((f: SelectedFileExtended) => f === file);
        this.filesForm.removeAt(fileIdx);
        this.updateFilesEvent.emit(this.filesForm.value.length ? this.filesForm.value : null);
    }

    public saveSetupData(setupImageData: SelectedFileExtended, i: number): void {
        this.filesForm.controls[i].patchValue({ ...this.files[i], ...setupImageData });
        this.updateFilesEvent.emit(this.filesForm.value.length ? this.filesForm.value : null);
    }

    public getImagePreviewSrc(file: SelectedFileExtended): string {
        const sources = this.ctx.buildFilePreviewUrls(file);
        const key = this.getImagePreviewKey(file);
        this.resetImagePreviewIndexOnSourcesChange(key, sources);

        const index = this.imagePreviewIndexes.get(key) || 0;

        return sources[index] || sources[0] || '';
    }

    public showNextImagePreview(file: SelectedFileExtended): void {
        const sources = this.ctx.buildFilePreviewUrls(file);
        const key = this.getImagePreviewKey(file);
        this.resetImagePreviewIndexOnSourcesChange(key, sources);

        const index = this.imagePreviewIndexes.get(key) || 0;

        if (index < sources.length - 1) {
            this.imagePreviewIndexes.set(key, index + 1);
        }
    }

    public dropGroup(event: CdkDragDrop<any[]>): void {
        const files = [...this.files];

        if (event.previousIndex === event.currentIndex) {
            return;
        }

        moveItemInArray(files, event.previousIndex, event.currentIndex);
        this.filesForm.patchValue(files);
        this.updateFilesEvent.emit(this.filesForm.value.length ? this.filesForm.value : null);
    }

    public trackFn(idx: any, file: SelectedFileExtended) {
        return file.url;
    }

    private resetImagePreviewIndexOnSourcesChange(key: string, sources: string[]): void {
        const sourceKey = sources.join('\n');

        if (this.imagePreviewSourceKeys.get(key) === sourceKey) {
            return;
        }

        this.imagePreviewSourceKeys.set(key, sourceKey);
        this.imagePreviewIndexes.set(key, 0);
    }

    private getImagePreviewKey(file: SelectedFileExtended): string {
        return file?.path || file?.url || file?.name || '';
    }
}
